import {ChangeDetectionStrategy, Component, computed, inject, signal} from '@angular/core';
import {BaseModalComponent} from "../../../../../../core/components/modals/base-modal/base-modal.component";
import {competitorArticlesResourceType} from "./configs/competitor-articles-modal-config";
import {CompetitorArticlesDialogData} from "./types/competitor-articles.types";
import {MAT_DIALOG_DATA} from "@angular/material/dialog";
import {
  MatExpansionModule
} from "@angular/material/expansion";
import {MatButton} from "@angular/material/button";
import {
  CompetitorArticlesFormComponent
} from "./components/competitor-articles-form/competitor-articles-form.component";
import {FormGroup} from "@angular/forms";
import {
  CompetitorArticlesAccordionTitleComponent
} from "./components/competitor-articles-accordion-title/competitor-articles-accordion-title.component";
import {LoadingService} from "../../../../../../core/services/loading.service";
import {CompetitorArticleData} from "./types/competitor-article-data.types";
import {CompetitorArticlesService} from "./services/competitor-articles.service";
import {CompetitorArticlesFormField} from "./types/competitor-articles-form-field.types";
import {competitorArticlesFormFields} from "./configs/competitor-articles-form-fields";
import {defaultCompetitorArticleData} from "./configs/default-competitor-article-data";
import {SelectData} from "../../../../../../core/types/records-table.types";
import {LoaderComponent} from "../../../../../../shared/components/loader/loader.component";

@Component({
  selector: 'app-competitor-articles-modal',
  imports: [
    BaseModalComponent,
    MatButton,
    MatExpansionModule,
    CompetitorArticlesFormComponent,
    CompetitorArticlesAccordionTitleComponent,
    LoaderComponent,
  ],
  templateUrl: './competitor-articles-modal.component.html',
  styleUrl: './competitor-articles-modal.component.scss',
  providers: [ LoadingService ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CompetitorArticlesModalComponent extends BaseModalComponent {
  private competitorArticlesDialogData = inject<CompetitorArticlesDialogData>(MAT_DIALOG_DATA);
  private competitorArticlesService: CompetitorArticlesService = inject(CompetitorArticlesService);

  public readonly competitorArticlesResourceType = competitorArticlesResourceType;
  public competitorArticleFormGroups = signal<Array<FormGroup>>([]);
  public competitorArticlesData = signal<Array<CompetitorArticleData>>([]);
  public isValid = signal<boolean>(false);
  public competitorBrandId = signal<number>(0);
  public productGroupSelectData = signal<Array<SelectData>>([]);
  public noFirstData = computed(() => this.competitorArticlesData().length > 1);

  public override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    await this.initModal();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(competitorArticlesResourceType.key);
  }

  public async handleUpdateData(data: CompetitorArticleData, index: number): Promise<void> {
    this.competitorArticlesData()[index] = data;
    this.competitorArticleFormGroups().at(index)?.patchValue(data);
    this.isValid.set(!!data.id);
  }

  public async handleDeleteData(index: number): Promise<void> {
    this.competitorArticlesData.update(data => {
      const newData = [...data];
      newData.splice(index, 1);
      return newData;
    });

    this.competitorArticleFormGroups.update(groups => {
      const newGroups = [...groups];
      newGroups.splice(index, 1);
      return newGroups;
    });

    if (!this.competitorArticlesData().length) {
      this.handleAddNew();
      return;
    }

    this.isValid.set(true);
  }

  public handleAddNew(): void {
    const formGroup = this.baseModalService.getFormGroup<CompetitorArticlesFormField>(competitorArticlesFormFields);
    this.competitorArticleFormGroups.update(forms  => [ ...forms, formGroup]);
    this.competitorArticlesData.update(data  => [ ...data, defaultCompetitorArticleData ]);
    this.isValid.set(false);
  }

  private async initModal(): Promise<void> {
    this.modalStatesService.openModal(competitorArticlesResourceType.key);
    this.loadingService.setIsLoadingState(true);
    this.competitorBrandId.set(this.competitorArticlesDialogData.parentId);
    await this.competitorArticlesService.fetchProductGroupSelectData()
      .then((data) => this.productGroupSelectData.set(data));
    await this.competitorArticlesService.fetchCompetitorArticlesData(this.competitorBrandId())
      .then(data => this.successFetch(data))
      .finally(() => this.loadingService.setIsLoadingState(false));
  }

  private successFetch(data: Array<CompetitorArticleData>): void {
    this.competitorArticlesData.set(data);
    this.isValid.set(!!data.length);
    this.initForm();
  }

  private initForm(): void {
    if (!this.competitorArticlesData().length) {
      this.handleAddNew();
      return;
    }

    const formGroups = this.competitorArticlesData().map(data => {
      const formGroup = this.baseModalService.getFormGroup<CompetitorArticlesFormField>(competitorArticlesFormFields);
      formGroup.patchValue(data);
      return formGroup;
    });

    this.competitorArticleFormGroups.set(formGroups);
  }
}
